import React, { useRef, useEffect } from 'react';
import { Box, Typography, Container, Card, CardContent } from '@mui/material';
import { motion } from 'framer-motion';
import SEO from '../../components/SEO';
import THEME from '../../constants/theme';

// Import logos
import octopusLogo from '../../assets/logos/octopus-horizontal-purple.svg';
import sentinelLogo from '../../assets/logos/sentinel-horizontal-blue.png';

const LoginPage = ({ setRefs }) => {
  const loginRef = useRef(null);

  useEffect(() => {
    setRefs({ login: loginRef });
  }, [setRefs]);

  const handleCardClick = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const cardVariants = {
    initial: { 
      opacity: 0, 
      y: 30,
      scale: 0.95
    },
    animate: { 
      opacity: 1, 
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    },
    hover: {
      y: -8,
      scale: 1.02,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    }
  };

  const logoVariants = {
    initial: { opacity: 0, scale: 0.8 },
    animate: { 
      opacity: 1, 
      scale: 1,
      transition: {
        duration: 0.5,
        delay: 0.2
      }
    },
    hover: {
      scale: 1.05,
      transition: {
        duration: 0.2
      }
    }
  };

  return (
    <>
      <SEO
        title="Login Portal"
        description="Access your OctopusSync or Sentinel account through our secure login portal."
        keywords="login, account, signin, octopussync, sentinel, portal"
      />
      
      <Box 
        ref={loginRef}
        id="login"
        sx={{ 
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          paddingRight: { xs: 0, md: '250px' },
          padding: { xs: '80px 20px 20px 20px', md: '40px 250px 40px 40px' },
          backgroundColor: 'var(--neutral-white)'
        }}
      >
        <Container maxWidth="lg">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <Typography 
              variant="h1" 
              sx={{ 
                mb: 2,
                textAlign: 'center',
                fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
                fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.SEMI_BOLD,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                color: THEME.COLORS.NEUTRAL_BLACK,
                background: 'linear-gradient(135deg, #6511af 0%, #8e44ad 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              Login Portal
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                textAlign: 'center',
                mb: 6,
                color: THEME.COLORS.NEUTRAL_GRAY,
                fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
                fontSize: '1.1rem',
                maxWidth: '600px',
                mx: 'auto'
              }}
            >
              Choose your service to access your account
            </Typography>
          </motion.div>

          {/* Cards Container */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: { xs: 3, md: 4 },
              maxWidth: '900px',
              mx: 'auto'
            }}
          >
            {/* OctopusSync Card */}
            <motion.div
              variants={cardVariants}
              initial="initial"
              animate="animate"
              whileHover="hover"
              style={{ height: '100%' }}
            >
              <Card
                onClick={() => handleCardClick('https://octopussync.com/octopussync')}
                sx={{
                  height: '100%',
                  cursor: 'pointer',
                  background: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(101, 17, 175, 0.2)',
                  borderRadius: THEME.BORDERS.RADIUS.LARGE,
                  boxShadow: '0 8px 32px rgba(101, 17, 175, 0.15)',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: '-50%',
                    left: '-50%',
                    width: '200%',
                    height: '200%',
                    background: 'radial-gradient(circle, rgba(101, 17, 175, 0.05) 0%, transparent 70%)',
                    pointerEvents: 'none'
                  },
                  '&:hover': {
                    borderColor: 'rgba(101, 17, 175, 0.4)',
                    boxShadow: '0 16px 48px rgba(101, 17, 175, 0.25)',
                    background: 'rgba(255, 255, 255, 0.95)'
                  }
                }}
              >
                <CardContent
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    padding: { xs: '40px 20px', md: '60px 40px' },
                    position: 'relative',
                    zIndex: 2,
                    height: '100%'
                  }}
                >
                  {/* OctopusSync Logo */}
                  <motion.div
                    variants={logoVariants}
                    initial="initial"
                    animate="animate"
                    whileHover="hover"
                    style={{ 
                      marginBottom: '30px',
                      width: '250px',
                      height: '80px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Box
                      component="img"
                      src={octopusLogo}
                      alt="OctopusSync"
                      sx={{
                        height: { xs: '60px', md: '80px' },
                        width: 'auto',
                        maxWidth: '250px',
                        filter: 'drop-shadow(0 4px 8px rgba(101, 17, 175, 0.2))'
                      }}
                    />
                  </motion.div>

                  <Typography
                    variant="body2"
                    sx={{
                      color: THEME.COLORS.NEUTRAL_GRAY,
                      fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
                      lineHeight: 1.6,
                      mb: 3,
                      fontSize: '0.95rem'
                    }}
                  >
                    Synchronize your trading power
                  </Typography>

                  <Box
                    sx={{
                      mt: 'auto',
                      padding: '12px 32px',
                      borderRadius: '25px',
                      background: 'linear-gradient(135deg, #6511af 0%, #8e44ad 100%)',
                      color: 'white',
                      fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
                      fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.MEDIUM,
                      fontSize: '0.9rem',
                      boxShadow: '0 4px 16px rgba(101, 17, 175, 0.3)'
                    }}
                  >
                    Login
                  </Box>
                </CardContent>
              </Card>
            </motion.div>

            {/* Sentinel Card */}
            <motion.div
              variants={cardVariants}
              initial="initial"
              animate="animate"
              whileHover="hover"
              style={{ height: '100%' }}
            >
              <Card
                onClick={() => handleCardClick('https://octopussync.vercel.app/sentinel')}
                sx={{
                  height: '100%',
                  cursor: 'pointer',
                  background: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(59, 130, 246, 0.2)',
                  borderRadius: THEME.BORDERS.RADIUS.LARGE,
                  boxShadow: '0 8px 32px rgba(59, 130, 246, 0.15)',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: '-50%',
                    left: '-50%',
                    width: '200%',
                    height: '200%',
                    background: 'radial-gradient(circle, rgba(59, 130, 246, 0.05) 0%, transparent 70%)',
                    pointerEvents: 'none'
                  },
                  '&:hover': {
                    borderColor: 'rgba(59, 130, 246, 0.4)',
                    boxShadow: '0 16px 48px rgba(59, 130, 246, 0.25)',
                    background: 'rgba(255, 255, 255, 0.95)'
                  }
                }}
              >
                <CardContent
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    padding: { xs: '40px 20px', md: '60px 40px' },
                    position: 'relative',
                    zIndex: 2,
                    height: '100%'
                  }}
                >
                  {/* Sentinel Logo */}
                  <motion.div
                    variants={logoVariants}
                    initial="initial"
                    animate="animate"
                    whileHover="hover"
                    style={{ 
                      marginBottom: '30px',
                      width: '250px',
                      height: '80px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Box
                      component="img"
                      src={sentinelLogo}
                      alt="Sentinel"
                      sx={{
                        height: { xs: '50px', md: '65px' },
                        width: 'auto',
                        maxWidth: '220px',
                        filter: 'drop-shadow(0 4px 8px rgba(59, 130, 246, 0.2))'
                      }}
                    />
                  </motion.div>

                  <Typography
                    variant="body2"
                    sx={{
                      color: THEME.COLORS.NEUTRAL_GRAY,
                      fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
                      lineHeight: 1.6,
                      mb: 3,
                      fontSize: '0.95rem'
                    }}
                  >
                    Advanced Risk Management and Monitoring Investment Accounts
                  </Typography>

                  <Box
                    sx={{
                      mt: 'auto',
                      padding: '12px 32px',
                      borderRadius: '25px',
                      background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                      color: 'white',
                      fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
                      fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.MEDIUM,
                      fontSize: '0.9rem',
                      boxShadow: '0 4px 16px rgba(59, 130, 246, 0.3)'
                    }}
                  >
                    Login
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default LoginPage;
